import styled from "@emotion/styled";

export const Section = styled.section<{ dimmed: boolean }>`
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  background-color: #ffffff;
  border-radius: 24px;
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.15);
  padding: 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  color: #0a043c;
  opacity: ${(props) => (props.dimmed ? 0.6 : 1)};
  transition: opacity 0.2s;

  @media (max-width: 600px) {
    padding: 1.25rem 1rem;
  }
`;

export const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

export const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;

  h2 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
  }
`;

export const TitleIcon = styled.span`
  width: 28px;
  height: 28px;
  border: 2px solid #046af3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

export const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;

  .p-multiselect {
    width: 12rem;
    height: 44px;
    border-radius: 8px;
    border: 1px solid #c9d3e3;
    align-items: center;
  }

  .p-multiselect-label-container {
    overflow: hidden;
  }

  .p-multiselect-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const SortButton = styled.button<{ isActive: boolean }>`
  min-height: 44px;
  padding: 0 1rem;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.isActive ? "#046af3" : "#c9d3e3")};
  background: ${(props) => (props.isActive ? "#eaf2fe" : "#ffffff")};
  color: ${(props) => (props.isActive ? "#0350b8" : "#0a043c")};
  font: inherit;
  font-size: 0.875rem;
  font-weight: ${(props) => (props.isActive ? 600 : 400)};
  cursor: pointer;
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

export const SummaryCard = styled.div`
  border: 1px solid #e3e8f2;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  span:first-of-type {
    font-size: 0.8125rem;
    color: #4a4f6a;
  }
`;

export const SummaryValue = styled.span<{ valueColor?: string }>`
  font-size: 1.625rem;
  font-weight: 800;
  color: ${(props) => props.valueColor ?? "#0a043c"};
`;

export const InfoIcon = styled.i`
  margin-left: 0.375rem;
  font-size: 0.8125rem;
  color: #8a90a8;
  vertical-align: middle;
`;

export const Chart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

export const Legend = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1.125rem;
  font-size: 0.8125rem;
  color: #4a4f6a;

  span {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }
`;

export const LegendColor = styled.span<{ swatch: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${(props) => props.swatch};
`;

export const ChartRow = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 180px) minmax(0, 1fr) 64px;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;

  > span:first-of-type {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > span:last-of-type {
    font-weight: 700;
    text-align: right;
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(80px, 110px) minmax(0, 1fr) 40px;
    gap: 0.5rem;
  }
`;

export const BarTrack = styled.div`
  display: flex;
  height: 22px;
  background: #f1f4f9;
  border-radius: 6px;
  overflow: hidden;
`;

export const BarSegment = styled.div<{ barColor: string; barWidth: number }>`
  width: ${(props) => props.barWidth}%;
  background: ${(props) => props.barColor};
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid #e3e8f2;
  border-radius: 12px;

  table {
    width: 100%;
    min-width: 760px;
    border-collapse: collapse;
    font-size: 0.875rem;
  }

  th {
    padding: 0.75rem 1rem;
    font-weight: 600;
    text-align: right;
    background: #f6f8fc;
  }

  th:first-of-type,
  th:last-of-type {
    text-align: left;
  }

  th:last-of-type {
    min-width: 180px;
  }

  td {
    padding: 0.75rem 1rem;
    text-align: right;
    border-top: 1px solid #e3e8f2;
  }

  td:first-of-type,
  td:last-of-type {
    text-align: left;
  }
`;

export const NumberCell = styled.td<{ valueColor?: string }>`
  color: ${(props) => props.valueColor ?? "inherit"};
  font-weight: ${(props) => (props.valueColor ? 700 : 400)};
`;

export const AttendantCell = styled.span`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

export const Avatar = styled.span<{ isSystem?: boolean }>`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${(props) => (props.isSystem ? "#e3e8f2" : "#d4e2f3")};
  color: ${(props) => (props.isSystem ? "#4a4f6a" : "#0350b8")};
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RateCell = styled.span`
  display: flex;
  align-items: center;
  gap: 0.625rem;

  > span:last-of-type {
    width: 42px;
    text-align: right;
    font-weight: 600;
  }
`;

export const RateTrack = styled.span`
  flex: 1;
  height: 8px;
  background: #f1f4f9;
  border-radius: 4px;
  overflow: hidden;
`;

export const RateBar = styled.span<{ barWidth: number }>`
  display: block;
  height: 100%;
  width: ${(props) => props.barWidth}%;
  background: #046af3;
`;

export const Footnote = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #4a4f6a;
`;
